"use client";

import { isToday, isThisWeek, isThisMonth } from "date-fns";
import {
  Plus,
  Search,
  Bell,
  CheckCircle,
  AlertTriangle,
  Clock,
  X,
  Trash2,
  CheckSquare,
} from "lucide-react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import React, { useState, useMemo, useEffect } from "react";

import { NewReminderDialog } from "@/app/(authenticated)/reminders/_modules/components/NewReminderDialog";
import { ReminderCard } from "@/app/(authenticated)/reminders/_modules/components/ReminderCard";
import { ReminderFiltersComponent } from "@/app/(authenticated)/reminders/_modules/components/ReminderFilters";
import { SentimentalsSection } from "@/app/(authenticated)/reminders/_modules/components/SentimentalsSection";
import {
  useReminders,
  useDeleteReminder,
  useUpdateReminder,
} from "@/app/(authenticated)/reminders/_modules/hooks/use-reminders";
import { backgroundTasks } from "@/app/(authenticated)/reminders/_modules/services/background-tasks";
import { Button } from "@/app/_module/components/ui/button";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { Input } from "@/app/_module/components/ui/input";
import { toast } from "@/app/_module/hooks/use-toast";
import type {
  ReminderUI,
  ReminderFilters as ReminderFiltersType,
  ReminderStatus,
} from "@/app/_module/types/reminders";

import { ConfirmDeleteModal } from "./_modules/components/confirmDelete";
import ViewerDisabled from "@/app/_module/components/ViewerDisabled";

const Reminders = function () {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<ReminderFiltersType>({
    status: "all",
    dateRange: "all",
    entityType: undefined,
    type: undefined,
    priority: undefined,
  });
  // Track if user has explicitly selected a stat card filter (not just default state)
  const [activeStatCard, setActiveStatCard] = useState<string | null>(null);

  // Memoize filters to prevent infinite loops
  const memoizedFilters = useMemo(() => filters, [
    filters.status,
    filters.dateRange,
    filters.entityType,
    filters.type,
    filters.priority,
  ]);
  const [showNewReminderDialog, setShowNewReminderDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState<
    ReminderUI | undefined
  >();

  // Handle URL action param for opening new reminder dialog
  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "new") {
      setEditingReminder(undefined);
      setShowNewReminderDialog(true);
      // Strip the query param IN PLACE — this page is also embedded at
      // /m/reminders on the native shell; a hardcoded "/reminders" would
      // yank mobile users onto the desktop route.
      router.replace(pathname || "/reminders");
    }
  }, [searchParams, router, pathname]);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [loadingReminderId, setLoadingReminderId] = useState<string | null>(
    null
  );

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showMassDeleteConfirm, setShowMassDeleteConfirm] = useState(false);
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);

  // Initialize background tasks when component mounts
  useEffect(() => {
    // Start background overdue checking
    backgroundTasks.startOverdueChecker(15); // Check every 15 minutes
      
    // Cleanup when component unmounts
    return () => {
      backgroundTasks.stopOverdueChecker();
    };
  }, []);

  // Fetch all reminders - status (including overdue) is managed by the backend
  const {
    data: remindersData,
    isLoading,
    error,
  } = useReminders({
    entityType: filters.entityType,
    refreshInterval: 60000,      // Refresh to pick up backend status changes
  });

  const deleteReminderMutation = useDeleteReminder();
  const updateReminderMutation = useUpdateReminder();

  // Get all reminders, deduplicate, and sort by status priority then due date
  const allReminders = useMemo(() => {
    const reminders = remindersData?.items || [];

    // Deduplicate reminders by ID
    const uniqueReminders = reminders.filter(
      (reminder, index, array) =>
        array.findIndex((r) => r.id === reminder.id) === index
    );

    // Status priority: pending/notified at top, completed/cancelled/overdue below
    const getStatusPriority = (status: string) => {
      switch (status) {
        case "pending":
        case "notified":
          return 0; // Top priority - active reminders
        case "completed":
        case "cancelled":
        case "overdue":
        default:
          return 1; // Lower priority - finished reminders
      }
    };

    // Sort by status priority first, then by dueDate + reminderTime (soonest first within each group)
    return uniqueReminders.sort((a, b) => {
      // First sort by status priority
      const priorityA = getStatusPriority(a.status);
      const priorityB = getStatusPriority(b.status);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Within the same priority group, sort by dueDate + reminderTime (soonest first)
      const getDateTime = (reminder: ReminderUI) => {
        const date = new Date(reminder.dueDate);
        if (reminder.reminderTime) {
          const time = new Date(reminder.reminderTime);
          date.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
        }
        return date.getTime();
      };

      return getDateTime(a) - getDateTime(b); // Ascending order (soonest first)
    });
  }, [remindersData?.items]);

  // Get total count
  const totalCount = remindersData?.meta?.totalItems || allReminders.length;

  const filteredReminders = useMemo(() => {
    return allReminders.filter((reminder) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (
          !reminder.title.toLowerCase().includes(searchLower) &&
          !reminder.description?.toLowerCase().includes(searchLower) &&
          !reminder.associatedClientName?.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }

      // Status filter - Trust backend status completely
      if (filters.status && filters.status !== "all") {
        if (filters.status === "completed" && reminder.status !== "completed")
          return false;
        if (filters.status === "pending" && reminder.status !== "pending")
          return false;
        if (filters.status === "cancelled" && reminder.status !== "cancelled")
          return false;
        // For overdue filter, only show truly 'overdue' status (not 'notified')
        // 'notified' means notification was sent but user still has 10-min grace period
        if (filters.status === "overdue" && reminder.status !== "overdue")
          return false;
      }

      // Type filter
      if (filters.type && reminder.type !== filters.type) return false;

      // Priority filter
      if (filters.priority && reminder.priority !== filters.priority)
        return false;

      // Date range filter
      if (filters.dateRange && filters.dateRange !== "all") {
        if (filters.dateRange === "today" && !isToday(reminder.dueDate))
          return false;
        if (filters.dateRange === "week" && !isThisWeek(reminder.dueDate))
          return false;
        if (filters.dateRange === "month" && !isThisMonth(reminder.dueDate))
          return false;
      }

      return true;
    });
  }, [allReminders, searchTerm, memoizedFilters]);

  const stats = useMemo(() => {
    const total = totalCount;
    const completed = allReminders.filter(
      (r) => r.status === "completed"
    ).length;
    const pending = allReminders.filter((r) => r.status === "pending").length;
    const cancelled = allReminders.filter(
      (r) => r.status === "cancelled"
    ).length;
    // Only count 'overdue' status as overdue (not 'notified')
    // 'notified' means notification was sent but user still has 10-min grace period
    const overdue = allReminders.filter(
      (r) => r.status === "overdue"
    ).length;
    const dueToday = allReminders.filter(
      (r) => isToday(r.dueDate) && r.status !== "completed"
    ).length;

    return { total, completed, pending, cancelled, overdue, dueToday };
  }, [allReminders, totalCount]);

  // Handle status toggle (backward compatibility)
  const handleCompleteReminder = (id: string) => {
    const reminder = allReminders.find((r) => r.id === id);
    if (reminder) {
      const newStatus: ReminderStatus =
        reminder.status === "completed" ? "pending" : "completed";
      handleStatusChange(id, newStatus);
    }
  };

  // New function to handle status changes using the status endpoint
  const handleStatusChange = (id: string, newStatus: ReminderStatus) => {
    // Prevent duplicate calls
    if (loadingReminderId === id || updateReminderMutation.isPending) {
      return;
    }
    
    setLoadingReminderId(id);

    // Use the regular update mutation with statusOnly flag
    updateReminderMutation.mutate(
      {
        reminderId: id,
        reminderData: { status: newStatus },
        statusOnly: true,
      },
      {
        onSuccess: () => {
          toast({
            title: "Status updated",
            description: `Reminder status changed to ${newStatus.replace("-", " ")}.`,
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update reminder status.",
            variant: "destructive",
          });
        },
        onSettled: () => {
          setLoadingReminderId(null);
        },
      }
    );
  };

  // New function to handle priority changes
  const handlePriorityChange = (
    id: string,
    newPriority: "urgent" | "high" | "medium" | "low"
  ) => {
    // Prevent duplicate calls
    if (loadingReminderId === id || updateReminderMutation.isPending) {
      return;
    }

    setLoadingReminderId(id);

    // Use the regular update mutation with the priority field (statusOnly = true for priority)
    updateReminderMutation.mutate(
      {
        reminderId: id,
        reminderData: { priority: newPriority },
        statusOnly: true, // Priority changes should use statusOnly = true
      },
      {
        onSuccess: () => {
          toast({
            title: "Priority updated",
            description: `Reminder priority changed to ${newPriority}.`,
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update reminder priority.",
            variant: "destructive",
          });
        },
        onSettled: () => {
          setLoadingReminderId(null);
        },
      }
    );
  };

  // New function to handle rescheduling cancelled reminders
  const handleReschedule = (
    id: string,
    newDate: Date,
    newTime: Date,
    reminder: ReminderUI
  ) => {
    // Prevent duplicate calls
    if (loadingReminderId === id || updateReminderMutation.isPending) {
      return;
    }

    setLoadingReminderId(id);

    // Update the reminder with new date, time, and set status back to pending
    // Include title and other required fields from the existing reminder
    updateReminderMutation.mutate(
      {
        reminderId: id,
        reminderData: {
          title: reminder.title, // Required for convertUIToApi
          dueDate: newDate,
          reminderTime: newTime,
          status: "pending",
          // Preserve existing fields
          description: reminder.description,
          type: reminder.type,
          priority: reminder.priority,
        },
        statusOnly: false, // This is a full update, not just status
      },
      {
        onSuccess: () => {
          toast({
            title: "Rescheduled",
            description: "Reminder has been rescheduled successfully.",
          });
        },
        onError: (error) => {
          console.error("Reschedule error:", error);
          toast({
            title: "Error",
            description: "Failed to reschedule reminder.",
            variant: "destructive",
          });
        },
        onSettled: () => {
          setLoadingReminderId(null);
        },
      }
    );
  };

  const handleEditReminder = (reminder: ReminderUI) => {
    setEditingReminder(reminder);
    setShowNewReminderDialog(true);
  };

  const handleDeleteReminder = (id: string) => {
    setDeleteTargetId(id);
  };

  // Selection mode handlers
  const handleSelect = (id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredReminders.map((r) => r.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleSelectionMode = () => {
    if (selectionMode) {
      // Exiting selection mode - clear selections
      setSelectedIds(new Set());
    }
    setSelectionMode(!selectionMode);
  };

  const handleMassDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsDeletingMultiple(true);
    const idsToDelete = Array.from(selectedIds);
    let successCount = 0;
    let failCount = 0;

    for (const id of idsToDelete) {
      try {
        await new Promise<void>((resolve, reject) => {
          deleteReminderMutation.mutate(id, {
            onSuccess: () => {
              successCount++;
              resolve();
            },
            onError: () => {
              failCount++;
              reject();
            },
          });
        });
      } catch {
        // Error already counted
      }
    }

    setIsDeletingMultiple(false);
    setShowMassDeleteConfirm(false);
    setSelectedIds(new Set());
    setSelectionMode(false);

    if (successCount > 0) {
      toast({
        title: "Reminders deleted",
        description: `Successfully deleted ${successCount} reminder${successCount > 1 ? "s" : ""}.${failCount > 0 ? ` Failed to delete ${failCount}.` : ""}`,
      });
    } else if (failCount > 0) {
      toast({
        title: "Delete failed",
        description: `Failed to delete ${failCount} reminder${failCount > 1 ? "s" : ""}.`,
        variant: "destructive",
      });
    }
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      dateRange: "all",
      entityType: undefined,
      type: undefined,
      priority: undefined,
    });
    setSearchTerm("");
    setActiveStatCard(null); // Reset stat card highlight
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Error loading reminders
        </h3>
        <p className="text-gray-600">
          There was an error loading your reminders. Please try again.
        </p>
      </div>
    );
  }

  const isMutating =
    deleteReminderMutation.isPending || updateReminderMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div data-tour="reminders-header" className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Reminders</h1>
          <p className="text-gray-600 mt-1">
            Manage your tasks, follow-ups, and important dates.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={toggleSelectionMode}
            size="sm"
            variant={selectionMode ? "secondary" : "outline"}
          >
            {selectionMode ? (
              <>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </>
            ) : (
              <>
                <CheckSquare className="h-4 w-4 mr-1" />
                Select
              </>
            )}
          </Button>
          {!selectionMode && (
            <ViewerDisabled message="Upgrade to create reminders">
              <Button data-tour="add-reminder" onClick={() => setShowNewReminderDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Reminder
              </Button>
            </ViewerDisabled>
          )}
        </div>
      </div>

      {/* Stats Cards - Updated to remove in-progress */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 lg:gap-4">
        <div
          className={`bg-white p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${activeStatCard === "total" ? "ring-2 ring-blue-500" : ""}`}
          onClick={() => {
            // Toggle off if already selected, otherwise select
            if (activeStatCard === "total") {
              setActiveStatCard(null);
            } else {
              setActiveStatCard("total");
            }
            setFilters(prev => ({ ...prev, status: "all", dateRange: "all" }));
          }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bell className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-xl font-semibold">{stats.total}</p>
            </div>
          </div>
        </div>

        <div
          className={`bg-white p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${activeStatCard === "completed" ? "ring-2 ring-green-500" : ""}`}
          onClick={() => {
            if (activeStatCard === "completed") {
              setActiveStatCard(null);
              setFilters(prev => ({ ...prev, status: "all", dateRange: "all" }));
            } else {
              setActiveStatCard("completed");
              setFilters(prev => ({ ...prev, status: "completed", dateRange: "all" }));
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-xl font-semibold">{stats.completed}</p>
            </div>
          </div>
        </div>

        <div
          className={`bg-white p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${activeStatCard === "pending" ? "ring-2 ring-yellow-500" : ""}`}
          onClick={() => {
            if (activeStatCard === "pending") {
              setActiveStatCard(null);
              setFilters(prev => ({ ...prev, status: "all", dateRange: "all" }));
            } else {
              setActiveStatCard("pending");
              setFilters(prev => ({ ...prev, status: "pending", dateRange: "all" }));
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-xl font-semibold">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div
          className={`bg-white p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${activeStatCard === "cancelled" ? "ring-2 ring-gray-500" : ""}`}
          onClick={() => {
            if (activeStatCard === "cancelled") {
              setActiveStatCard(null);
              setFilters(prev => ({ ...prev, status: "all", dateRange: "all" }));
            } else {
              setActiveStatCard("cancelled");
              setFilters(prev => ({ ...prev, status: "cancelled", dateRange: "all" }));
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <X className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Cancelled</p>
              <p className="text-xl font-semibold">{stats.cancelled}</p>
            </div>
          </div>
        </div>

        <div
          className={`bg-white p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${activeStatCard === "overdue" ? "ring-2 ring-red-500" : ""}`}
          onClick={() => {
            if (activeStatCard === "overdue") {
              setActiveStatCard(null);
              setFilters(prev => ({ ...prev, status: "all", dateRange: "all" }));
            } else {
              setActiveStatCard("overdue");
              setFilters(prev => ({ ...prev, status: "overdue", dateRange: "all" }));
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Overdue</p>
              <p className="text-xl font-semibold">{stats.overdue}</p>
            </div>
          </div>
        </div>

        <div
          className={`bg-white p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${activeStatCard === "today" ? "ring-2 ring-orange-500" : ""}`}
          onClick={() => {
            if (activeStatCard === "today") {
              setActiveStatCard(null);
              setFilters(prev => ({ ...prev, status: "all", dateRange: "all" }));
            } else {
              setActiveStatCard("today");
              setFilters(prev => ({ ...prev, status: "all", dateRange: "today" }));
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Due Today</p>
              <p className="text-xl font-semibold">{stats.dueToday}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sentimentals Section */}
      <SentimentalsSection />

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            className="pl-10"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search reminders..."
            value={searchTerm}
          />
        </div>

        <div data-tour="reminder-filters">
          <ReminderFiltersComponent
            filters={filters}
            onClearFilters={clearFilters}
            onFiltersChange={setFilters}
          />
        </div>
      </div>

      {/* Selection Bar - shown when in selection mode */}
      {selectionMode && filteredReminders.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={
                filteredReminders.length > 0 &&
                selectedIds.size === filteredReminders.length
              }
              onCheckedChange={(checked) => handleSelectAll(!!checked)}
              className="h-5 w-5"
            />
            <span className="text-sm text-gray-700">
              {selectedIds.size === 0
                ? "Select all"
                : `${selectedIds.size} of ${filteredReminders.length} selected`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowMassDeleteConfirm(true)}
                disabled={isDeletingMultiple}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete ({selectedIds.size})
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Reminders List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="text-gray-600 mt-2">Loading reminders...</p>
          </div>
        ) : filteredReminders.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No reminders found
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ||
              Object.values(filters).some((v) => v && v !== "all")
                ? "Try adjusting your search or filters."
                : "Create your first reminder to get started."}
            </p>
            <Button onClick={() => setShowNewReminderDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Reminder
            </Button>
          </div>
        ) : (
          <div data-tour="reminder-list" className="space-y-3">
            {filteredReminders.map((reminder) => (
              <React.Fragment key={reminder.id}>
                <ReminderCard
                  data-tour="reminder-actions"
                  isLoading={!!isMutating && loadingReminderId === reminder.id}
                  isSelected={selectedIds.has(reminder.id)}
                  onComplete={handleCompleteReminder}
                  onDelete={handleDeleteReminder}
                  onEdit={handleEditReminder}
                  onPriorityChange={handlePriorityChange}
                  onReschedule={handleReschedule}
                  onSelect={handleSelect}
                  onStatusChange={handleStatusChange}
                  reminder={reminder}
                  selectionMode={selectionMode}
                />
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {!!deleteTargetId && (
        <ConfirmDeleteModal
          itemName="this reminder"
          onClose={() => setDeleteTargetId(null)}
          open={!!deleteTargetId}
          onConfirm={() => {
            if (deleteTargetId) {
              setLoadingReminderId(deleteTargetId);
              deleteReminderMutation.mutate(deleteTargetId, {
                onSuccess: () => {
                  toast({
                    title: "Reminder deleted",
                    description: "The reminder has been removed successfully.",
                  });
                  setDeleteTargetId(null);
                },
                onError: () => {
                  toast({
                    title: "Error deleting reminder",
                    description: "Something went wrong. Please try again.",
                  });
                  setDeleteTargetId(null);
                },
                onSettled: () => {
                  setTimeout(() => setLoadingReminderId(null), 200);
                },
              });
            }
          }}
        />
      )}

      {/* Mass Delete Confirmation Modal */}
      {showMassDeleteConfirm && (
        <ConfirmDeleteModal
          itemName={`${selectedIds.size} reminder${selectedIds.size > 1 ? "s" : ""}`}
          onClose={() => setShowMassDeleteConfirm(false)}
          open={showMassDeleteConfirm}
          onConfirm={handleMassDelete}
        />
      )}

      {/* New/Edit Reminder Dialog */}
      <NewReminderDialog
        editingReminder={editingReminder}
        open={showNewReminderDialog}
        onOpenChange={(open) => {
          setShowNewReminderDialog(open);
          if (!open) {
            // Clear editing reminder when dialog closes
            setTimeout(() => {
              setEditingReminder(undefined);
            }, 100);
          }
        }}
      />
    </div>
  );
};

export default Reminders;